import { BaseActionObject, StateNodeConfig, assign } from "xstate4"
import {
  Context,
  StudyPlanTargets,
  StudyPlanV2Schema,
  Transitions
} from "../types"
import {
  spv2ProgressState,
  spv2SettingsState,
  spv2AdminManageStudyPlansState,
  spv2ProgressByContentState,
  spv2ProgressByStudentState,
  spv2TeacherOverallState
} from "./states"
import { checkSPV2CurrentTargetAction, selectSubjectAction } from "./actions"
import {
  BASE_SP_V2_PATH,
  BASE_SP_V2_PROGRESS_PATH,
  BASE_SP_V2_SETTINGS_PATH,
  BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH,
  BASE_SP_V2_PROGRESS_BY_CONTENT_PATH,
  BASE_SP_V2_PROGRESS_BY_STUDENT_PATH,
  BASE_SP_V2_TEACHER_OVERALL_PATH
} from "./constants"
import {
  checkSPV2MultipleFiltersTarget,
  getSPV2SubjectsStudyPlansTransition
} from "./transitionalStates"
import { checkFiltersForMultipleValues } from "./guards"

const studyPlanV2State: StateNodeConfig<
  Context,
  StudyPlanV2Schema,
  Transitions,
  BaseActionObject
> = {
  id: "study_plan_v2",
  entry: assign(checkSPV2CurrentTargetAction),
  initial: "loading",
  states: {
    loading: { ...getSPV2SubjectsStudyPlansTransition },

    idle: {
      always: [
        {
          target: "check_multiple_filters_target",
          cond: ctx =>
            checkFiltersForMultipleValues(
              ctx.reportingFiltersState,
              ctx.currentTarget
            )
        },
        {
          target: BASE_SP_V2_PROGRESS_PATH,
          cond: ctx => ctx.currentTarget === StudyPlanTargets.Progress
        },
        {
          target: BASE_SP_V2_SETTINGS_PATH,
          cond: ctx => ctx.currentTarget === StudyPlanTargets.Settings
        },
        {
          target: BASE_SP_V2_PROGRESS_BY_CONTENT_PATH,
          cond: ctx => ctx.currentTarget === StudyPlanTargets.ProgressByContent
        },
        {
          target: BASE_SP_V2_PROGRESS_BY_STUDENT_PATH,
          cond: ctx => ctx.currentTarget === StudyPlanTargets.ProgressByStudent
        },
        {
          target: BASE_SP_V2_TEACHER_OVERALL_PATH,
          cond: ctx => ctx.currentTarget === StudyPlanTargets.TeacherOverall
        },
        {
          target: "#StudyPlan.teacher_view",
          actions: assign<Context>(() => ({
            previousParentState: "teacher_view",
            currentTarget: StudyPlanTargets.TeacherView
          })),
          cond: ctx =>
            [
              StudyPlanTargets.TeacherView,
              StudyPlanTargets.ManageStudyPlans
            ].includes(ctx.currentTarget as StudyPlanTargets) &&
            ctx.user.role === "Teacher"
        },
        {
          target: BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH,
          cond: ctx =>
            ctx.currentTarget === StudyPlanTargets.ManageStudyPlans &&
            ["District Admin", "School Admin"].includes(ctx.user.role)
        },
        {
          target: BASE_SP_V2_PROGRESS_PATH,
          actions: assign<Context>({ currentTarget: StudyPlanTargets.Progress })
        }
      ]
    },

    check_multiple_filters_target: { ...checkSPV2MultipleFiltersTarget },

    sp_v2_progress_state: { ...spv2ProgressState },
    sp_v2_settings_state: { ...spv2SettingsState },
    sp_v2_admin_manage_study_plans_state: { ...spv2AdminManageStudyPlansState },
    sp_v2_progress_by_content_state: { ...spv2ProgressByContentState },
    sp_v2_progress_by_student_state: { ...spv2ProgressByStudentState },
    sp_v2_teacher_overall_state: { ...spv2TeacherOverallState }
  },
  on: {
    SELECT_SUBJECT: {
      target: `${BASE_SP_V2_PATH}.idle`,
      actions: assign(selectSubjectAction)
    }
  }
}

export default studyPlanV2State
